import apiClient from './apiClient';

class PatientMedicalService {
  
  // =====================================================================
  // MEDICATION METHODS
  // =====================================================================
  
  /**
   * Get all medications for a patient
   * @param {number} patientId - The patient's internal ID
   * @param {object} params - Query parameters
   * @param {boolean} params.active_only - Return only active medications
   * @param {string} params.status_filter - Filter by medication status
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to return
   */
  async getPatientMedications(patientId, params = {}) {
    try {
      const response = await apiClient.get(`/patient-medical-test/patients/${patientId}/medications/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient medications:', error);
      throw error;
    }
  }

  /**
   * Get paginated medications for a patient
   * @param {number} patientId - The patient's internal ID
   * @param {object} params - Query parameters for pagination
   */
  async getPatientMedicationsPaginated(patientId, params = {}) {
    try {
      const response = await apiClient.get(`/patient-medical/patients/${patientId}/medications/paginated/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching paginated medications:', error);
      throw error;
    }
  }

  /**
   * Get only active medications for a patient
   * @param {number} patientId - The patient's internal ID
   */
  async getActiveMedications(patientId) {
    return this.getPatientMedications(patientId, { active_only: true });
  }

  /**
   * Create a new medication record
   * @param {object} medicationData - The medication data
   */
  async createMedication(medicationData) {
    try {
      const response = await apiClient.post('/patient-medical/medications/', medicationData);
      return response.data;
    } catch (error) {
      console.error('Error creating medication:', error);
      throw error;
    }
  }

  /**
   * Update a medication record
   * @param {number} medicationId - The medication ID
   * @param {object} updates - The updates to apply
   */
  async updateMedication(medicationId, updates) {
    try {
      const response = await apiClient.put(`/patient-medical/medications/${medicationId}/`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating medication:', error);
      throw error;
    }
  }

  /**
   * Discontinue a medication
   * @param {number} medicationId - The medication ID
   * @param {string} endDate - Optional end date (defaults to today)
   */
  async discontinueMedication(medicationId, endDate = null) {
    try {
      const params = endDate ? { end_date: endDate } : {};
      const response = await apiClient.patch(`/patient-medical/medications/${medicationId}/discontinue/`, {}, { params });
      return response.data;
    } catch (error) {
      console.error('Error discontinuing medication:', error);
      throw error;
    }
  }

  /**
   * Delete a medication record
   * @param {number} medicationId - The medication ID
   */
  async deleteMedication(medicationId) {
    try {
      const response = await apiClient.delete(`/patient-medical/medications/${medicationId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting medication:', error);
      throw error;
    }
  }

  /**
   * Search medications for a patient
   * @param {number} patientId - The patient's internal ID
   * @param {string} searchTerm - The search term
   * @param {number} limit - Maximum number of results
   */
  async searchMedications(patientId, searchTerm, limit = 20) {
    try {
      const response = await apiClient.get(`/patient-medical/patients/${patientId}/medications/search/`, {
        params: { q: searchTerm, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching medications:', error);
      throw error;
    }
  }

  // =====================================================================
  // ALLERGY METHODS
  // =====================================================================

  /**
   * Get all allergies for a patient
   * @param {number} patientId - The patient's internal ID
   * @param {object} params - Query parameters
   */
  async getPatientAllergies(patientId, params = {}) {
    try {
      const response = await apiClient.get(`/patient-medical-test/patients/${patientId}/allergies/`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient allergies:', error);
      throw error;
    }
  }

  /**
   * Get only active allergies for a patient
   * @param {number} patientId - The patient's internal ID
   */
  async getActiveAllergies(patientId) {
    return this.getPatientAllergies(patientId, { active_only: true });
  }

  /**
   * Get critical allergies for a patient
   * @param {number} patientId - The patient's internal ID
   */
  async getCriticalAllergies(patientId) {
    try {
      const response = await apiClient.get(`/patient-medical-test/patients/${patientId}/allergies/critical/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching critical allergies:', error);
      throw error;
    }
  }

  /**
   * Get drug allergies for a patient
   * @param {number} patientId - The patient's internal ID
   */
  async getDrugAllergies(patientId) {
    try {
      const response = await apiClient.get(`/patient-medical-test/patients/${patientId}/allergies/drug/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching drug allergies:', error);
      throw error;
    }
  }

  /**
   * Get allergies by type for a patient
   * @param {number} patientId - The patient's internal ID
   * @param {string} allergyType - The type of allergy ('Drug', 'Food', 'Environmental', etc.)
   */
  async getAllergiesByType(patientId, allergyType) {
    return this.getPatientAllergies(patientId, { allergy_type_filter: allergyType, active_only: true });
  }

  /**
   * Create a new allergy record
   * @param {object} allergyData - The allergy data
   */
  async createAllergy(allergyData) {
    try {
      const response = await apiClient.post('/patient-medical-test/allergies/', allergyData);
      return response.data;
    } catch (error) {
      console.error('Error creating allergy:', error);
      throw error;
    }
  }

  /**
   * Update an allergy record
   * @param {number} allergyId - The allergy ID
   * @param {object} updates - The updates to apply
   */
  async updateAllergy(allergyId, updates) {
    try {
      const response = await apiClient.put(`/patient-medical-test/allergies/${allergyId}/`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating allergy:', error);
      throw error;
    }
  }

  /**
   * Delete an allergy record
   * @param {number} allergyId - The allergy ID
   */
  async deleteAllergy(allergyId) {
    try {
      const response = await apiClient.delete(`/patient-medical-test/allergies/${allergyId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting allergy:', error);
      throw error;
    }
  }

  /**
   * Search allergies for a patient
   * @param {number} patientId - The patient's internal ID
   * @param {string} searchTerm - The search term
   * @param {number} limit - Maximum number of results
   */
  async searchAllergies(patientId, searchTerm, limit = 20) {
    try {
      const response = await apiClient.get(`/patient-medical-test/patients/${patientId}/allergies/search/`, {
        params: { q: searchTerm, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching allergies:', error);
      throw error;
    }
  }

  // =====================================================================
  // MEDICATION CRUD METHODS
  // =====================================================================

  /**
   * Create a new medication record
   * @param {object} medicationData - The medication data
   */
  async createMedication(medicationData) {
    try {
      const response = await apiClient.post('/patient-medical-test/medications/', medicationData);
      return response.data;
    } catch (error) {
      console.error('Error creating medication:', error);
      throw error;
    }
  }

  /**
   * Update a medication record
   * @param {number} medicationId - The medication ID
   * @param {object} updates - The updates to apply
   */
  async updateMedication(medicationId, updates) {
    try {
      const response = await apiClient.put(`/patient-medical-test/medications/${medicationId}/`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating medication:', error);
      throw error;
    }
  }

  /**
   * Delete a medication record
   * @param {number} medicationId - The medication ID
   */
  async deleteMedication(medicationId) {
    try {
      const response = await apiClient.delete(`/patient-medical-test/medications/${medicationId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting medication:', error);
      throw error;
    }
  }

  // =====================================================================
  // REFERENCE DATA METHODS
  // =====================================================================

  /**
   * Get medication options for dropdown
   */
  async getMedicationOptions() {
    try {
      const response = await apiClient.get('/patient-medical-test/reference/medications/');
      return response.data.medications;
    } catch (error) {
      console.error('Error fetching medication options:', error);
      // Fallback to static list
      return [
        'Lisinopril', 'Metformin', 'Atorvastatin', 'Aspirin', 'Ibuprofen', 
        'Acetaminophen', 'Omeprazole', 'Simvastatin', 'Amlodipine'
      ];
    }
  }

  /**
   * Get dosage options for dropdown
   */
  async getDosageOptions() {
    try {
      const response = await apiClient.get('/patient-medical-test/reference/dosages/');
      return response.data.dosages;
    } catch (error) {
      console.error('Error fetching dosage options:', error);
      // Fallback to static list
      return [
        '5mg', '10mg', '20mg', '25mg', '50mg', '100mg', '250mg', '500mg', '1000mg'
      ];
    }
  }

  /**
   * Get frequency options for dropdown
   */
  async getFrequencyOptions() {
    try {
      const response = await apiClient.get('/patient-medical-test/reference/frequencies/');
      return response.data.frequencies;
    } catch (error) {
      console.error('Error fetching frequency options:', error);
      // Fallback to static list
      return [
        'Once daily', 'Twice daily', 'Three times daily', 'As needed', 'At bedtime'
      ];
    }
  }

  /**
   * Get allergy type options for dropdown
   */
  async getAllergyTypeOptions() {
    try {
      const response = await apiClient.get('/patient-medical-test/reference/allergy-types/');
      return response.data.allergy_types;
    } catch (error) {
      console.error('Error fetching allergy type options:', error);
      // Fallback to static list
      return ['Drug', 'Food', 'Environmental', 'Other'];
    }
  }

  /**
   * Get allergy severity options for dropdown
   */
  async getAllergySeverityOptions() {
    try {
      const response = await apiClient.get('/patient-medical-test/reference/allergy-severities/');
      return response.data.severities;
    } catch (error) {
      console.error('Error fetching allergy severity options:', error);
      // Fallback to static list
      return ['Mild', 'Moderate', 'Severe', 'Life-threatening'];
    }
  }

  // =====================================================================
  // MEDICAL SUMMARY METHODS
  // =====================================================================

  /**
   * Get comprehensive medical summary for a patient
   * @param {number} patientId - The patient's internal ID
   */
  async getPatientMedicalSummary(patientId) {
    try {
      const response = await apiClient.get(`/patient-medical-test/patients/${patientId}/medical-summary/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching medical summary:', error);
      throw error;
    }
  }

  // =====================================================================
  // UTILITY METHODS
  // =====================================================================

  /**
   * Get medical alerts for a patient (critical allergies and drug interactions)
   * @param {number} patientId - The patient's internal ID
   */
  async getMedicalAlerts(patientId) {
    try {
      const [criticalAllergies, activeMedications] = await Promise.all([
        this.getCriticalAllergies(patientId),
        this.getActiveMedications(patientId)
      ]);

      // Check for medications with drug interactions
      const medicationsWithInteractions = activeMedications.filter(med => 
        med.drug_interactions && med.drug_interactions.trim()
      );

      return {
        criticalAllergies,
        medicationsWithInteractions,
        hasAlerts: criticalAllergies.length > 0 || medicationsWithInteractions.length > 0
      };
    } catch (error) {
      console.error('Error fetching medical alerts:', error);
      throw error;
    }
  }

  /**
   * Get medications and allergies formatted for display in UI
   * @param {number} patientId - The patient's internal ID
   */
  async getMedicalDataForDisplay(patientId) {
    try {
      const summary = await this.getPatientMedicalSummary(patientId);
      
      // Separate allergies by type for display
      const drugAllergies = summary.all_allergies.filter(allergy => allergy.allergy_type === 'Drug');
      const otherAllergies = summary.all_allergies.filter(allergy => allergy.allergy_type !== 'Drug');

      return {
        medications: summary.active_medications,
        drugAllergies,
        otherAllergies,
        activeMedicationsCount: summary.active_medications.length,
        criticalAllergiesCount: summary.critical_allergies.length
      };
    } catch (error) {
      console.error('Error fetching medical data for display:', error);
      throw error;
    }
  }

  // =====================================================================
  // ENUM VALUES FOR FRONTEND
  // =====================================================================

  getMedicationStatuses() {
    return ['Active', 'Discontinued', 'Completed', 'On Hold'];
  }

  getAllergyTypes() {
    return ['Drug', 'Food', 'Environmental', 'Contact', 'Other'];
  }

  getAllergySeverities() {
    return ['Mild', 'Moderate', 'Severe', 'Life-threatening'];
  }

  getAllergyStatuses() {
    return ['Active', 'Resolved', 'Questioned'];
  }

  getMedicationRoutes() {
    return ['Oral', 'IV', 'IM', 'Topical', 'Inhalation', 'Subcutaneous', 'Rectal', 'Sublingual'];
  }

  getMedicationForms() {
    return ['Tablet', 'Capsule', 'Liquid', 'Injection', 'Cream', 'Ointment', 'Patch', 'Inhaler', 'Drops'];
  }
}

// Export singleton instance
export const patientMedicalService = new PatientMedicalService();
export default patientMedicalService;
